/**
 * Shared billing code identifier helpers.
 *
 * Keep these semantics aligned with the backend charges/code_id utility.
 *
 * Two tiers of strictness (GDN-2658):
 * - DISPLAY / lenient: `normalizeToCodeId` preserves legacy identifiers so the
 *   UI can keep rendering whatever was persisted (e.g. `CPT:CASH`, `NDC:...`).
 * - OUTBOUND WRITE / strict: `normalizeToCodeIdStrict` / `isValidCodeId`
 *   enforce the backend contract — `CPT:`/`HCPCS:` prefix plus a bare code
 *   matching ^[A-Z0-9]{5}$. Anything else must never be sent to the API.
 */

const HCPCS_PATTERN = /^[A-EGHJ-TVUX][0-9]{4}$/;
const SUPPORTED_CODE_SYSTEMS = new Set(['CPT', 'HCPCS']);

/**
 * Canonical constraint for a bare billing code (backend validates
 * `^[A-Z0-9]{5}$` after stripping the `CPT:`/`HCPCS:` prefix).
 */
export const BILLING_BARE_CODE_PATTERN = /^[A-Z0-9]{5}$/;

export const stripCodeSystem = (codeId: string): string => {
  const colonIndex = codeId.indexOf(':');
  return colonIndex !== -1 ? codeId.slice(colonIndex + 1) : codeId;
};

export const sanitizeBillingCodeInput = (value: string): string =>
  value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 5);

export const normalizeToCodeId = (code: string): string => {
  const trimmed = (code ?? '').trim().toUpperCase();
  if (!trimmed) return '';

  const colonIndex = trimmed.indexOf(':');
  if (colonIndex !== -1) {
    const suppliedSystem = trimmed.slice(0, colonIndex);
    const suppliedCode = trimmed.slice(colonIndex + 1).trim();
    if (!suppliedCode) return '';
    // Preserve unsupported legacy identifiers during unrelated edits. New
    // picker input cannot create these prefixes, and callers can surface
    // explicit validation instead of silently rewriting the identifier.
    if (!SUPPORTED_CODE_SYSTEMS.has(suppliedSystem)) return `${suppliedSystem}:${suppliedCode}`;

    const bare = sanitizeBillingCodeInput(suppliedCode);
    if (!bare) return '';
    const system = suppliedSystem === 'CPT' && HCPCS_PATTERN.test(bare) ? 'HCPCS' : suppliedSystem;
    return `${system}:${bare}`;
  }

  const bare = sanitizeBillingCodeInput(trimmed);
  if (!bare) return '';
  return HCPCS_PATTERN.test(bare) ? `HCPCS:${bare}` : `CPT:${bare}`;
};

/**
 * Whether a bare (prefix-less) billing code conforms to the backend contract.
 */
export const isValidBareCode = (code: string): boolean =>
  BILLING_BARE_CODE_PATTERN.test((code ?? '').trim().toUpperCase());

/**
 * Whether a full `SYSTEM:CODE` identifier conforms to the backend write
 * contract: a supported code system (CPT/HCPCS) plus a 5-char alphanumeric
 * bare code. Legacy sentinels (`CPT:CASH`, `CPT:95`, `CPT:`) and unsupported
 * systems (`NDC:...`) fail this check and must never be sent in a write.
 */
export const isValidCodeId = (codeId: string): boolean => {
  const trimmed = (codeId ?? '').trim().toUpperCase();
  const colonIndex = trimmed.indexOf(':');
  if (colonIndex === -1) return false;
  const system = trimmed.slice(0, colonIndex);
  const bare = trimmed.slice(colonIndex + 1);
  return SUPPORTED_CODE_SYSTEMS.has(system) && BILLING_BARE_CODE_PATTERN.test(bare);
};

/**
 * Strict variant of `normalizeToCodeId` for OUTBOUND WRITE payloads.
 *
 * Returns the canonical `SYSTEM:CODE` identifier, or `null` when the input
 * cannot be represented as a conforming code — too short (`95`), too long
 * (`992130` is NOT truncated here, unlike the lenient sanitize), an
 * unsupported system prefix, or empty. Callers must treat `null` as
 * "do not send", not as "send empty".
 */
export const normalizeToCodeIdStrict = (code: string): string | null => {
  const trimmed = (code ?? '').trim();
  if (!trimmed) return null;
  const colonIndex = trimmed.indexOf(':');
  const rawBare = colonIndex !== -1 ? trimmed.slice(colonIndex + 1) : trimmed;
  const cleanedBare = rawBare.toUpperCase().replace(/[^A-Z0-9]/g, '');
  // Validate on the un-truncated bare code so over-long input is rejected
  // instead of being silently clipped to five characters.
  if (!BILLING_BARE_CODE_PATTERN.test(cleanedBare)) return null;
  const normalized = normalizeToCodeId(trimmed);
  return normalized && isValidCodeId(normalized) ? normalized : null;
};
